/**
 * Unlock autoplay without playing product sounds (especially not ringing.mp3).
 * Call from a user-gesture handler or after startIncomingRingingSound.
 */

let unlockListenersAttached = false;
let unlockedByGesture = false;
const afterUnlock: Array<() => void> = [];

const SILENT_WAV_B64 = 'UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA=';

function silentWavBlobUrl(): string | null {
  try {
    const bin = atob(SILENT_WAV_B64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    return URL.createObjectURL(new Blob([bytes], { type: 'audio/wav' }));
  } catch {
    return null;
  }
}

function trySilentUnlock(): void {
  if (unlockedByGesture || typeof window === 'undefined') return;

  const finish = () => {
    if (unlockedByGesture) return;
    unlockedByGesture = true;
    const queued = afterUnlock.splice(0, afterUnlock.length);
    for (const fn of queued) fn();
  };

  try {
    const AC =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (AC) {
      const ctx = new AC();
      void ctx.resume().then(() => {
        void ctx.close();
        finish();
      });
    }
  } catch {
    /* try element path */
  }

  const blobUrl = silentWavBlobUrl();
  if (blobUrl) {
    try {
      const a = new Audio(blobUrl);
      a.muted = true;
      a.volume = 0;
      void a
        .play()
        .then(() => {
          a.pause();
          finish();
        })
        .catch(() => {
          /* wait for the next gesture */
        })
        .finally(() => {
          URL.revokeObjectURL(blobUrl);
        });
    } catch {
      URL.revokeObjectURL(blobUrl);
    }
  }
}

export function onMediaPlaybackUnlocked(fn: () => void): void {
  if (unlockedByGesture) {
    fn();
    return;
  }
  afterUnlock.push(fn);
}

export function attachMediaPlaybackUnlockOnGesture(): void {
  if (typeof document === 'undefined' || unlockListenersAttached) return;
  unlockListenersAttached = true;
  const onGesture = () => {
    trySilentUnlock();
  };
  document.addEventListener('pointerdown', onGesture, { capture: true, passive: true });
  document.addEventListener('keydown', onGesture, { capture: true, passive: true });
}

export function isMediaPlaybackUnlocked(): boolean {
  return unlockedByGesture;
}
